import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import { peerKeyVariants } from '../notifications/notifyPeerHints.ts'
import {
  clearPairedContactsMirror,
  loadPairedContactsMirror,
  mirrorPairedContact,
  removePairedContactFromMirror,
} from '../pairing/pairedContactsPersistence.ts'
import { saveContactPublicKeyBackup } from './keyStore.ts'

export interface ZionContact {
  contactId: string
  publicKey: string
  nickname: string
  pairedAt: number
  sessionToken: string
  inboundQueueId: string
  customName?: string
  /** Solo UI/lista — persistito in localStorage (zion_pinned_chats), non in IDB. */
  isPinned?: boolean
  pinnedAt?: number
  /** Anteprima ultimo messaggio (UI Home) — aggiornata da chatStore. */
  lastMessage?: string
  lastMessageTimestamp?: number
}

export interface PairingRequest {
  fromSessionToken: string
  publicKey: string
  nickname: string
  queueId?: string
}

interface PairingDb extends DBSchema {
  contacts: {
    key: string
    value: ZionContact
  }
  meta: {
    key: string
    value: string
  }
}

let dbPromise: Promise<IDBPDatabase<PairingDb>> | null = null

function getDb(): Promise<IDBPDatabase<PairingDb>> {
  if (!dbPromise) {
    dbPromise = openDB<PairingDb>('zion-pairing', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('contacts')) db.createObjectStore('contacts')
        if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta')
      },
    })
  }
  return dbPromise
}

export function contactDisplayName(c: ZionContact): string {
  const custom = c.customName?.trim()
  if (custom && !/^[a-f0-9-]{8,}$/i.test(custom)) return custom
  const nick = c.nickname?.trim()
  if (nick && !/^[a-f0-9-]{8,}$/i.test(nick)) return nick
  return 'Contatto'
}

export function contactHandle(c: {
  nickname?: string
  customName?: string
  contactId?: string
  sessionToken?: string
}): string {
  const raw =
    c.nickname?.trim() ||
    c.customName?.trim() ||
    c.sessionToken?.trim().slice(0, 8) ||
    c.contactId?.trim().slice(0, 8) ||
    'sconosciuto'
  return raw.startsWith('@') ? raw : `@${raw}`
}

export async function listContacts(): Promise<ZionContact[]> {
  const db = await getDb()
  return db.getAll('contacts')
}

export async function saveContact(contact: ZionContact): Promise<void> {
  const db = await getDb()
  await db.put('contacts', contact, contact.contactId)
  if (contact.publicKey?.trim()) {
    await saveContactPublicKeyBackup(contact.nickname, contact.sessionToken, contact.publicKey)
    mirrorPairedContact(contact)
  }
  notifyContactsChanged()
  void import('./contactsDbForSw.ts').then((m) => m.persistSingleContactForServiceWorker(contact))
}

/**
 * Ripristina contatti accoppiati dal mirror localStorage (senza TTL).
 * Chiamare al bootstrap prima di hydrate chiavi.
 */
export async function restorePairedContactsFromMirror(): Promise<number> {
  const mirrors = loadPairedContactsMirror()
  let restored = 0
  for (const m of mirrors) {
    const sess = m.sessionToken.trim()
    const pub = m.publicKey.trim()
    if (!sess || !pub) continue

    const existing =
      (await findContactBySession(sess)) ??
      (m.contactId ? await findContact(m.contactId) : null)

    if (existing) {
      const needsMerge =
        !existing.publicKey?.trim() ||
        (!existing.inboundQueueId?.trim() && m.inboundQueueId?.trim())
      if (needsMerge) {
        await saveContact({
          ...existing,
          publicKey: existing.publicKey?.trim() || pub,
          inboundQueueId: existing.inboundQueueId?.trim() || m.inboundQueueId || '',
          nickname: existing.nickname?.trim() || m.nickname || sess.slice(0, 8),
          customName: existing.customName ?? m.customName,
        })
        restored++
      }
      continue
    }

    await saveContact({
      contactId: m.contactId?.trim() || crypto.randomUUID().toLowerCase(),
      publicKey: pub,
      nickname: m.nickname?.trim() || sess.slice(0, 8),
      pairedAt: m.pairedAt || Date.now(),
      sessionToken: sess,
      inboundQueueId: m.inboundQueueId?.trim() || '',
      customName: m.customName,
    })
    restored++
  }
  return restored
}

/** Mutex per-contactId: aggiornamenti atomici senza interferire con altri contatti. */
const contactLocks = new Map<string, Promise<unknown>>()

export async function saveContactAtomic(
  contactId: string,
  mutator: (current: ZionContact | null) => Promise<ZionContact | null> | ZionContact | null,
): Promise<ZionContact | null> {
  const id = contactId.trim()
  if (!id) return null
  const prev = contactLocks.get(id) ?? Promise.resolve()
  let release!: () => void
  const gate = new Promise<void>((r) => {
    release = r
  })
  const chain = prev.then(() => gate)
  contactLocks.set(id, chain)
  await prev
  try {
    const current = await findContact(id)
    const next = await mutator(current)
    if (!next) return null
    await saveContact(next)
    return next
  } finally {
    release()
    if (contactLocks.get(id) === chain) contactLocks.delete(id)
  }
}

export async function renameContact(contactId: string, customName: string): Promise<ZionContact | null> {
  const contact = await findContact(contactId)
  if (!contact) return null
  const trimmed = customName.trim()
  const updated: ZionContact = {
    ...contact,
    customName: trimmed || undefined,
  }
  await saveContact(updated)
  void import('./contactsDbForSw.ts').then((m) => m.persistSingleContactForServiceWorker(updated))
  void import('../app/activeChatBridge.ts').then((m) => m.syncContactNamesToSw())
  return updated
}

type ContactListener = () => void
const contactListeners = new Set<ContactListener>()

export function onContactsChanged(fn: ContactListener): () => void {
  contactListeners.add(fn)
  return () => contactListeners.delete(fn)
}

function notifyContactsChanged(): void {
  for (const fn of contactListeners) fn()
}

function contactMatchesHint(c: ZionContact, hint: string): boolean {
  const h = hint.trim().toLowerCase()
  if (!h) return false
  return (
    c.contactId.trim().toLowerCase() === h ||
    c.sessionToken.trim().toLowerCase() === h ||
    c.nickname.trim().toLowerCase() === h
  )
}

export async function removeContact(contactId: string): Promise<void> {
  await removeContactCompletely(contactId)
}

/** Rimuove ogni contatto che corrisponde a id, sessionToken o nickname. */
export async function removeContactCompletely(hint: string): Promise<string[]> {
  const h = hint.trim()
  if (!h) return []
  const db = await getDb()
  const all = await db.getAll('contacts')
  const keys = new Set<string>()
  for (const c of all) {
    if (contactMatchesHint(c, h)) {
      keys.add(c.contactId)
    }
  }
  if (!keys.size && all.some((c) => c.contactId === h)) {
    keys.add(h)
  }
  const tx = db.transaction('contacts', 'readwrite')
  for (const key of keys) {
    await tx.store.delete(key)
  }
  await tx.done
  if (keys.size) {
    for (const key of keys) removePairedContactFromMirror(key)
    notifyContactsChanged()
  }
  return [...keys]
}

export async function findContact(contactId: string): Promise<ZionContact | null> {
  const db = await getDb()
  return (await db.get('contacts', contactId)) ?? null
}

export async function findContactBySession(sessionToken: string): Promise<ZionContact | null> {
  const token = sessionToken.trim().toLowerCase()
  const all = await listContacts()
  return all.find((c) => c.sessionToken.toLowerCase() === token) ?? null
}

/** Risolve contatto da qualsiasi hint push/socket (session, nickname, customName, queue). */
export async function findContactByHints(hints: string[]): Promise<ZionContact | null> {
  const variants = new Set<string>()
  for (const h of hints) {
    for (const v of peerKeyVariants(String(h || '').trim())) {
      if (v) variants.add(v)
    }
  }
  if (!variants.size) return null
  const all = await listContacts()
  for (const c of all) {
    for (const raw of [
      c.contactId,
      c.sessionToken,
      c.nickname,
      c.customName,
      c.inboundQueueId,
    ]) {
      const k = String(raw || '')
        .trim()
        .toLowerCase()
      if (!k) continue
      if (variants.has(k)) return c
      const alt = k.startsWith('@') ? k.slice(1) : `@${k}`
      if (variants.has(alt)) return c
    }
  }
  return null
}

/**
 * Aggiorna lastMessage + timestamp sul contatto (Home lista).
 * Risolve per contactId o sessionToken. Non tocca crypto/code.
 */
export async function touchContactLastMessage(
  contactKey: string,
  timestamp: number,
  lastMessage?: string,
): Promise<void> {
  const key = contactKey.trim()
  if (!key || !Number.isFinite(timestamp) || timestamp <= 0) return
  const contact = (await findContact(key)) ?? (await findContactBySession(key))
  if (!contact) return
  const preview = lastMessage?.trim()
  const prevTs = contact.lastMessageTimestamp ?? 0
  if (timestamp < prevTs) return
  if (timestamp === prevTs && (!preview || preview === (contact.lastMessage ?? ''))) return
  await saveContact({
    ...contact,
    lastMessageTimestamp: timestamp,
    ...(preview ? { lastMessage: preview } : {}),
  })
}

export async function updateContactInboundQueue(
  sessionToken: string,
  inboundQueueId: string,
): Promise<ZionContact | null> {
  const contact = await findContactBySession(sessionToken)
  if (!contact) return null
  const updated: ZionContact = { ...contact, inboundQueueId: inboundQueueId.trim() }
  await saveContact(updated)
  return updated
}

export async function upsertPairedContact(
  input: {
    sessionToken: string
    publicKey: string
    nickname: string
    inboundQueueId: string
  },
  opts?: { refreshPairedAt?: boolean },
): Promise<ZionContact> {
  const existing =
    (await findContactBySession(input.sessionToken)) ??
    (input.nickname ? (await listContacts()).find((c) => c.nickname === input.nickname) : null)

  const contact: ZionContact = {
    contactId: existing?.contactId ?? crypto.randomUUID().toLowerCase(),
    publicKey: input.publicKey,
    nickname: input.nickname || existing?.nickname || input.sessionToken.slice(0, 8),
    pairedAt: opts?.refreshPairedAt ? Date.now() : (existing?.pairedAt ?? Date.now()),
    sessionToken: input.sessionToken,
    inboundQueueId: input.inboundQueueId || existing?.inboundQueueId || '',
    customName: existing?.customName,
  }
  await saveContact(contact)
  return contact
}

export async function getPendingPairingRequest(): Promise<PairingRequest | null> {
  const db = await getDb()
  const raw = await db.get('meta', 'pending_pair')
  if (!raw) return null
  try {
    return JSON.parse(raw) as PairingRequest
  } catch {
    return null
  }
}

export async function replaceAllContacts(contacts: ZionContact[]): Promise<void> {
  const db = await getDb()
  await db.clear('contacts')
  for (const c of contacts) {
    await db.put('contacts', c, c.contactId)
  }
  notifyContactsChanged()
}

export async function clearAllContacts(): Promise<void> {
  const db = await getDb()
  await db.clear('contacts')
  clearPairedContactsMirror()
}

export async function setPendingPairingRequest(req: PairingRequest | null): Promise<void> {
  const db = await getDb()
  if (!req) {
    await db.delete('meta', 'pending_pair')
    return
  }
  await db.put('meta', JSON.stringify(req), 'pending_pair')
}
